import type { FormSelectOption } from "@/react/prop-types";
import type { Equipment } from "@procore/equipment-select";
import type {
   CommonFormValuesForRequestAndAssignment,
   CommonPayloadForRequestAndAssignment,
} from "../types";
import type { BannerProps } from "@procore/core-react";
import type { AssignmentWorkDays } from "@/models/assignment";
import type { SerializedAssignment } from "@laborchart-modules/common/dist/postgres/serializers/assignment-serializer";
import type { SerializedEquipmentAssignment } from "@laborchart-modules/common/dist/postgres/serializers/equipment-assignment-serializer";

export type AssignmentCallbackData = {
   assignments: Array<Omit<SerializedAssignment, "comments">>;
   equipmentAssignments: SerializedEquipmentAssignment[];
};

export type OverTimeRates = {
   sunday: number;
   monday: number;
   tuesday: number;
   wednesday: number;
   thursday: number;
   friday: number;
   saturday: number;
};

export type PaySplitRates = {
   overtime: number;
   straight: number;
   unpaid: number;
};

export type AssignmentFormValues = CommonFormValuesForRequestAndAssignment & {
   resource: Array<FormSelectOption<string>> | FormSelectOption<string> | null;
   equipment: Equipment[] | Equipment | null;
   overtime: boolean;
   overtime_rates: OverTimeRates | { [key: string]: number };
   pay_split: PaySplitRates;
};

export type SingleResourceAssignment = CommonPayloadForRequestAndAssignment & {
   resource_id: string;
};

export type BatchResourceAssignment = CommonPayloadForRequestAndAssignment & {
   resource_ids: string[];
};

export type SingleEquipmentAssignment = CommonPayloadForRequestAndAssignment & {
   resource_id: string;
};

export type BatchEquipmentAssignment = CommonPayloadForRequestAndAssignment & {
   resource_ids: string[];
};

export enum AssignmentFields {
   ALLOCATION = "assignment_by_allocation",
   TIME = "assignment_by_time",
}

export type AssignmentFormattedTypes = {
   assignmentId: string;
   formattedAssignmentDetails: string;
   start_day: number;
};

export type CurrentAssignmentsBannerProps = {
   bodyContent?: number;
   icon?: string;
   titleContent?: number;
   variant: BannerProps["variant"];
   expandableContentMaxHeight?: number;
   expandableContentLines?: number;
   personIds: string[];
   personNames: string[];
   showBanner: boolean;
};

export type EquipmentAssignmentsBannerProps = Omit<
   CurrentAssignmentsBannerProps,
   "personIds" | "personNames"
> & {
   equipmentResourceId: string;
   equipmentName: string;
   equipmentIdentificationNumber: string;
};

export type EditAssignmentFormValues = Omit<
   CommonFormValuesForRequestAndAssignment,
   | "assignment_by_time"
   | "assignment_by_allocation"
   | "work_days"
   | "instruction_text"
   | "work_scope_text"
   | "comments"
   | "author_id"
   | "category"
   | "subcategory"
   | "start_time"
   | "end_time"
> & {
   start_time: FormSelectOption<number> | null;
   end_time: FormSelectOption<number> | null;
   resource: FormSelectOption<string> | null;
};

export type AssignmentListData = {
   id: string;
   start_day: number;
   end_day: number;
   start_time: string;
   end_time: string;
   person: {
      name: {
         first: string;
         last: string;
      };
      profile_pic_url: string | null;
   };
   project: string;
   project_id: string;
   status: {
      color: string;
      id: string;
      label: string;
   } | null;
   start_date: string;
   end_date: string;
   percent_allocated: number | null;
   work_days: string | null;
   work_days_object: AssignmentWorkDays | null;
};

export type UpdateAssignmentsPayload = {
   id: string;
   [key: string]: unknown;
};

export type FormattedAssignmentData = {
   id: string;
   project_name: string;
   project_id: string;
   job_title: {
      label: string;
      color: string;
      shape: string;
      id: string;
   };
   start_date: Date;
   end_date: Date;
};
